import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage/session';

import applicationReducer from './application/application';
import { formReducer, invalidateForm } from './applicationform';
import { authenticationReducer, invalidateAuthentication } from './authentication';
import { settingsReducer } from './settings';
import wizardReducer from './wizard/wizard';

const persistConfig = (key: string) => ({
    key,
    storage,
});

const persistedAuthenticationReducer = persistReducer(persistConfig('rootAuthentication'), authenticationReducer);
const persistedApplicationReducer = persistReducer(persistConfig('rootApplication'), applicationReducer);

const persistedWizardReducer = persistReducer(persistConfig('rootWizard'), wizardReducer);
const persistedSettingsReducer = persistReducer(persistConfig('rootSettings'), settingsReducer);
const persistedFormReducer = persistReducer(persistConfig('rootForm'), formReducer);

const consumerloanReducer = {
    authentication: persistedAuthenticationReducer,
    application: persistedApplicationReducer,
    wizard: persistedWizardReducer,
    settings: persistedSettingsReducer,
    form: persistedFormReducer,
};

export const rootReducer = combineReducers({
    ...consumerloanReducer,
});

export const createStore = (preloadedState?: Partial<ReturnType<typeof rootReducer>>) =>
    configureStore({
        reducer: rootReducer,
        devTools: process.env.NODE_ENV !== 'production',
        middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false }),
        preloadedState,
    });

export const store = createStore();

// Not awaited: invalidateAuthentication clears authentication state synchronously up front (see its own
// doc comment), so awaiting the full thunk isn't needed for that -- and would mean a hung revoke_token call
// blocks invalidateForm() below indefinitely, along with every caller of invalidateStore that awaits it
// (e.g. InitCoApplicantPage, which specifically needs this to finish before navigating).
export const invalidateStore = async (dispatch: AppDispatch) => {
    dispatch(invalidateAuthentication());
    dispatch(invalidateForm());
};

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = typeof createStore;
export type AppDispatch = typeof store.dispatch;
